import { z } from 'zod';

import {
    type DynamicWidgetIssue,
    getDynamicWidgetSourceId,
    type ResourceShiftListWidgetModel,
    resourceShiftListWidgetModelSchema,
    type ShiftListWidgetDayGroup,
    type ShiftListWidgetRow,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import { splitShiftDatetime } from '@apps/web/components/phoebe_chat_view/tool_calls/shift_datetime';

const OPEN_SHIFT_STATUSES: ReadonlySet<string> = new Set([
    'open',
    'needs_coverage',
]);

export const legacyShiftOutputSchema = z
    .object({
        shifts: z.array(z.unknown()),
    })
    .passthrough();

const legacyShiftRowSchema = z
    .object({
        shift_id: z.string().min(1),
        patient_name: z.string().min(1),
    })
    .passthrough();

export const nullableShiftStringSchema = z.string().nullable();
export const nullableShiftNumberSchema = z.number().finite().nullable();
export const shiftStringArraySchema = z.array(z.string()).readonly();

const futureOccurrenceSchema = z
    .object({
        shift_id: z.string().min(1),
    })
    .passthrough();

const entitySchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
});

const outreachStateSummarySchema = z.object({
    active: z.boolean(),
    paused: z.boolean(),
    by_status: z.record(z.string(), z.number().int().nonnegative()),
    by_type: z.record(z.string(), z.number().int().nonnegative()),
    latest_status: z.string().nullable(),
    latest_type: z.string().nullable(),
});

const artifactShiftRowSchema = z.object({
    shift_id: z.string().min(1),
    client: entitySchema.nullable(),
    start_time: z.string().min(1),
    end_time: z.string().min(1),
    status: z.string().min(1),
    assigned_caregiver: entitySchema.nullable(),
    outreach_state_summary: outreachStateSummarySchema.optional(),
});

export type LegacyShiftRowDecode =
    | {
          readonly success: true;
          readonly row: Record<string, unknown> & {
              readonly shift_id: string;
              readonly patient_name: string;
          };
      }
    | {
          readonly success: false;
          readonly field: string;
          readonly rawValue: unknown;
      };

export function decodeLegacyShiftRow(rawRow: unknown): LegacyShiftRowDecode {
    const parsed = legacyShiftRowSchema.safeParse(rawRow);
    if (parsed.success) {
        return { success: true, row: parsed.data };
    }
    const firstIssue = parsed.error.issues[0];
    const field =
        typeof firstIssue?.path[0] === 'string' ? firstIssue.path[0] : 'row';
    const rawValue =
        typeof rawRow === 'object' && rawRow !== null && !Array.isArray(rawRow)
            ? (rawRow as Record<string, unknown>)[field]
            : null;
    return { success: false, field, rawValue };
}

export function decodeFutureOccurrenceId(value: unknown): string | null {
    const parsed = futureOccurrenceSchema.safeParse(value);
    return parsed.success ? parsed.data.shift_id : null;
}

export function isActionableShiftStatus(status: string | null): boolean {
    return status !== null && OPEN_SHIFT_STATUSES.has(status);
}

export function formatShiftDayLabel(day: string | null): string {
    if (!day) {
        return 'Date not shown';
    }
    const parsed = new Date(`${day}T00:00:00Z`);
    if (Number.isNaN(parsed.getTime())) {
        return day;
    }
    const weekday = parsed.toLocaleDateString('en-US', {
        weekday: 'short',
        timeZone: 'UTC',
    });
    const monthDay = parsed.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    });
    return `${weekday} · ${monthDay}`;
}

// A shift that crosses midnight ends under a different day heading, so the
// end time carries a short date ("6:00 AM (Aug 8)") instead of the raw
// ISO day the row data stores.
function formatCrossDayEndLabel(
    day: string,
    time: string | null | undefined,
): string {
    const parsed = new Date(`${day}T00:00:00Z`);
    const monthDay = Number.isNaN(parsed.getTime())
        ? day
        : parsed.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
          });
    return time ? `${time} (${monthDay})` : monthDay;
}

export function formatLegacyShiftTimeLabel(
    startTime: string | null,
    endTime: string | null,
): string | null {
    const start = splitShiftDatetime(startTime);
    const end = splitShiftDatetime(endTime);
    const endLabel =
        end.day && end.day !== start.day
            ? formatCrossDayEndLabel(end.day, end.time)
            : end.time;
    if (!start.time && !endLabel) {
        return null;
    }
    return `${start.time ?? ''}${
        start.time && endLabel ? ' – ' : ''
    }${endLabel ?? ''}`;
}

function localIsoParts(value: string): {
    readonly day: string;
    readonly time: string;
} | null {
    const match =
        /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?$/.exec(
            value,
        );
    if (match === null) {
        return null;
    }
    const hour = Number(match[2]);
    const minute = match[3];
    if (hour > 23 || minute === undefined || Number(minute) > 59) {
        return null;
    }
    const meridiem = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return {
        day: match[1] ?? '',
        time: `${displayHour}:${minute} ${meridiem}`,
    };
}

export function decodeArtifactShiftRow(
    rawRow: unknown,
): ShiftListWidgetRow | null {
    const parsed = artifactShiftRowSchema.safeParse(rawRow);
    if (!parsed.success) {
        return null;
    }
    const row = parsed.data;
    const start = localIsoParts(row.start_time);
    const end = localIsoParts(row.end_time);
    const endLabel =
        start !== null && end !== null && start.day !== end.day
            ? formatCrossDayEndLabel(end.day, end.time)
            : end?.time;
    const timeLabel =
        start?.time || endLabel
            ? `${start?.time ?? ''}${start?.time && endLabel ? ' – ' : ''}${
                  endLabel ?? ''
              }`
            : null;
    return {
        shiftId: row.shift_id,
        patientName: row.client?.name ?? 'Client not available',
        day: start?.day ?? null,
        timeLabel,
        city: null,
        status: row.status,
        frequency: null,
        caregiverId: row.assigned_caregiver?.id ?? null,
        caregiverName: row.assigned_caregiver?.name ?? null,
        seriesOpen: null,
        seriesCovered: null,
        roleRequired: null,
        skillsRequired: [],
        futureOccurrenceIds: [],
        occurrenceIds: [row.shift_id],
        ...(row.outreach_state_summary === undefined
            ? {}
            : {
                  outreachState: {
                      active: row.outreach_state_summary.active,
                      paused: row.outreach_state_summary.paused,
                      byStatus: row.outreach_state_summary.by_status,
                      byType: row.outreach_state_summary.by_type,
                      latestStatus: row.outreach_state_summary.latest_status,
                      latestType: row.outreach_state_summary.latest_type,
                  },
              }),
    };
}

export function groupShiftRowIndexesByDay(
    rows: ReadonlyArray<ShiftListWidgetRow>,
): Array<{
    readonly day: string | null;
    readonly rowIndexes: Array<number>;
}> {
    const groups: Array<{
        readonly day: string | null;
        readonly rowIndexes: Array<number>;
    }> = [];
    rows.forEach((row, rowIndex) => {
        const lastGroup = groups[groups.length - 1];
        if (lastGroup?.day === row.day) {
            lastGroup.rowIndexes.push(rowIndex);
            return;
        }
        groups.push({ day: row.day, rowIndexes: [rowIndex] });
    });
    return groups;
}

export function buildShiftDayGroups(
    rows: ReadonlyArray<ShiftListWidgetRow>,
): Array<ShiftListWidgetDayGroup> {
    return groupShiftRowIndexesByDay(rows).map((group) => ({
        ...group,
        dayLabel: formatShiftDayLabel(group.day),
    }));
}

export function getActionableShiftRows(
    rows: ReadonlyArray<ShiftListWidgetRow>,
): Array<ShiftListWidgetRow> {
    return rows.filter(
        (row) =>
            isActionableShiftStatus(row.status) && row.occurrenceIds.length > 0,
    );
}

export function collectShiftOccurrenceIds(
    rows: ReadonlyArray<ShiftListWidgetRow>,
): Array<string> {
    return Array.from(new Set(rows.flatMap((row) => row.occurrenceIds)));
}

export type ShiftArtifactHydrationOptions = {
    readonly sourceKind: 'event_source' | 'resource_source';
    readonly bindingId: string;
    readonly issuePrefix: string;
    readonly rowProvenance: 'event_preview' | 'resource';
};

const DEFAULT_SHIFT_ARTIFACT_HYDRATION_OPTIONS = {
    sourceKind: 'resource_source',
    bindingId: 'live.rows',
    issuePrefix: 'workspace-row',
    rowProvenance: 'resource',
} as const;

export function hydrateShiftArtifactModel(
    model: ResourceShiftListWidgetModel,
    rawRows: ReadonlyArray<unknown>,
    options?: ShiftArtifactHydrationOptions,
): ResourceShiftListWidgetModel;
export function hydrateShiftArtifactModel(
    model: ResourceShiftListWidgetModel,
    rawRows: ReadonlyArray<unknown>,
    options: ShiftArtifactHydrationOptions = DEFAULT_SHIFT_ARTIFACT_HYDRATION_OPTIONS,
): ResourceShiftListWidgetModel {
    const rows: Array<ShiftListWidgetRow> = [];
    const invalidRowIssues: Array<DynamicWidgetIssue> = [];
    const rowSource = model.sources.find(
        (source) => source.kind === options.sourceKind,
    );
    const rowSourceId =
        rowSource === undefined ? null : getDynamicWidgetSourceId(rowSource);
    rawRows.forEach((rawRow, sourceRowIndex) => {
        const row = decodeArtifactShiftRow(rawRow);
        if (row !== null) {
            rows.push(row);
            return;
        }
        invalidRowIssues.push({
            id: `${model.widgetId}.${options.issuePrefix}-${sourceRowIndex}`,
            code: 'source_invalid',
            level: 'warning',
            scope: 'section',
            bindingIds: [options.bindingId],
            sourceIds: rowSourceId === null ? [] : [rowSourceId],
            retryable: false,
            safeDetails: { rowIndex: sourceRowIndex },
        });
    });
    const actionableRows = getActionableShiftRows(rows);
    const hydratedModel = {
        ...model,
        rows,
        dayGroups: buildShiftDayGroups(rows),
        openShiftCount: actionableRows.length,
        bulkOpenShiftCount: actionableRows.length,
        bulkShiftIds: collectShiftOccurrenceIds(actionableRows),
        issues: [
            ...model.issues.filter(
                (issue) =>
                    !issue.id.startsWith(`${model.widgetId}.preview-row-`) &&
                    !issue.id.startsWith(`${model.widgetId}.workspace-row-`),
            ),
            ...invalidRowIssues,
        ],
        rowProvenance: options.rowProvenance,
    };
    return resourceShiftListWidgetModelSchema.parse(hydratedModel);
}
